<template>
  <RfDialog
    ref="dialogRef"
    :title="isRefine ? 'Import Designs from Figma' : 'Import from Figma'"
    width="600"
    keepOpenOnConfirm
    :cancelText="step === Step.DONE ? 'Close' : 'Cancel'"
    v-bind="{ ...handleConfirmButton }"
    @close="close"
  >
    <div v-if="step === Step.FORM" class="flex flex-col gap-6">
      <p class="text-body text-grey-1">
        Design your web popup in Figma using our official template and AI will automatically
        translate it into a styled Recurly Engage prompt.
        <a
          href="https://docs.recurly.com/recurly-engage/docs/figma"
          class="underline!"
          target="_blank"
          rel="noopener noreferrer"
          >Learn more</a
        >
      </p>

      <RfInput
        v-if="!isExistingPrompt"
        v-model="form.name"
        variant="outlined"
        requiredTitle
        title="Prompt Name"
        placeholder="My Figma Prompt"
        :errorMessage="visibleErrors.name || ''"
        @blur="touched.name = true"
      />

      <div class="flex flex-col gap-2">
        <h3 class="text-heading-2">1. Duplicate the template to your Figma workspace</h3>
        <p class="text-body text-grey-1">
          Open our template in Figma and duplicate it to your workspace so you can edit it.
        </p>
        <RfButtonNew
          type="tertiary"
          size="small"
          href="https://www.figma.com/community/file/1660045412615829580"
          rel="noopener noreferrer"
          class="mt-1 self-start"
        >
          Open Figma Template
          <RfOpenInNewIcon class="rf-icon h-4 w-4" />
        </RfButtonNew>
      </div>

      <div class="flex flex-col gap-2">
        <h3 class="text-heading-2">2. Customize the Desktop and Mobile frames</h3>
        <p class="text-body text-grey-1">
          Update the colors, typography, and content using the provided frames as your starting
          point. Refer to the template's <span class="font-semibold">About</span> page for guidance.
        </p>
      </div>

      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <h3 class="text-heading-2">3. Paste your frame links and import</h3>
          <p class="text-body text-grey-1">
            When your design is ready, copy each frame link (⌘+L on Mac, Ctrl+L on Windows) and
            paste it below. At least one link is required.
          </p>
        </div>

        <RfInput
          v-model="form.figmaUrl"
          variant="outlined"
          title="Desktop Frame Link"
          placeholder="https://www.figma.com/design/..."
          :errorMessage="visibleErrors.figmaUrl || ''"
          @blur="touched.figmaUrl = true"
        />

        <RfInput
          v-model="form.mobileFigmaUrl"
          variant="outlined"
          title="Mobile Frame Link"
          hintMessage="No mobile frame? Reuse your desktop link."
          placeholder="https://www.figma.com/design/..."
          :errorMessage="visibleErrors.mobileFigmaUrl || ''"
          @blur="touched.mobileFigmaUrl = true"
        />
      </div>
    </div>

    <div v-else class="flex flex-col items-center gap-4 py-6">
      <RfSpinnerIcon v-if="step === Step.POLLING" class="fill-beige-50 h-14 w-14 animate-spin" />
      <RfCheckIcon v-else-if="step === Step.DONE" class="h-14 w-14 fill-green-50" />
      <RfStatusIcon v-else-if="step === Step.ERROR" class="fill-vermillion-50 h-14 w-14" />

      <div class="flex flex-col items-center gap-1">
        <p class="text-heading-2 text-grey-1">{{ stepMessage }}</p>
        <p v-if="step === Step.POLLING" class="text-body text-beige-50 capitalize">
          {{ humanStatus }}
        </p>
      </div>

      <p v-if="step === Step.ERROR" class="text-vermillion-50 text-body max-w-xs text-center">
        {{ errorMessage }}
      </p>
    </div>
  </RfDialog>
</template>

<script setup>
import { computed, onUnmounted, reactive, ref, useTemplateRef } from "vue";
import { useRouter } from "vue-router";
import RfDialog from "@/components/modals/RfDialog.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import RfInput from "@/components/inputs/RfInput.vue";
import RfSpinnerIcon from "@/components/icons/RfSpinnerIcon.vue";
import RfCheckIcon from "@/components/icons/RfCheckIcon.vue";
import RfStatusIcon from "@/components/icons/RfStatusIcon.vue";
import RfOpenInNewIcon from "@/components/icons/RfOpenInNewIcon.vue";
import ApiFigma from "@/apis/ApiFigma";

const Step = Object.freeze({
  FORM: "form",
  POLLING: "polling",
  DONE: "done",
  ERROR: "error",
});

const Mode = Object.freeze({
  IMPORT: "import",
  REFINE: "refine",
});

const TERMINAL = new Set(["completed", "failed", "stale"]);
const POLL_INTERVAL_MS = 1500;
const MAX_POLL_ATTEMPTS = 120;
const FIGMA_URL_PREFIX = "https://www.figma.com/";

const STATUS_LABELS = {
  queued: "Queued",
  processing: "Processing",
  fetching_figma: "Fetching Figma design",
  mapping_actions: "Mapping actions",
  resolving_background: "Uploading background",
  creating_path_group: "Creating prompt group",
  submitting_to_redfast: "Creating prompt",
};

const props = defineProps({
  app: { type: Object, required: true },
  pathGroupId: { type: String, default: null },
  pathType: { type: String, default: "retention_modal" },
  deviceType: { type: String, default: "web" },
  mode: { type: String, default: "import", validator: v => ["import", "refine"].includes(v) },
  promptId: { type: String, default: null },
  initialName: { type: String, default: null },
  initialFigmaUrl: { type: String, default: null },
  initialMobileFigmaUrl: { type: String, default: null },
});

const emit = defineEmits(["done"]);

const router = useRouter();
const dialogRef = useTemplateRef("dialogRef");

const step = ref(Step.FORM);
const form = ref({ name: "Figma Import", figmaUrl: "", mobileFigmaUrl: "" });
const submitted = ref(false);
const touched = reactive({ name: false, figmaUrl: false, mobileFigmaUrl: false });
const workflowId = ref(null);
const resultPathId = ref(null);
const statusDetail = ref(null);
const errorMessage = ref(null);
const pollTimer = ref(null);
const pollCount = ref(0);

const isRefine = computed(() => props.mode === Mode.REFINE);
// True when importing into an existing prompt (either refine mode or create-with-path_id).
// Controls UI copy and name-field visibility; the API call is still determined by isRefine.
const isExistingPrompt = computed(() => isRefine.value || !!props.promptId);

const fieldErrors = computed(() => {
  const errs = { name: null, figmaUrl: null, mobileFigmaUrl: null, links: null };
  const desktop = form.value.figmaUrl.trim();
  const mobile = form.value.mobileFigmaUrl.trim();
  if (!isExistingPrompt.value && !form.value.name.trim()) {
    errs.name = "Prompt name is required.";
  }
  if (desktop && !desktop.startsWith(FIGMA_URL_PREFIX)) {
    errs.figmaUrl = "Please enter a valid Figma URL (https://www.figma.com/...).";
  }
  if (mobile && !mobile.startsWith(FIGMA_URL_PREFIX)) {
    errs.mobileFigmaUrl = "Please enter a valid Figma URL (https://www.figma.com/...).";
  }
  if (!desktop && !mobile) {
    errs.links = "Enter at least one frame link.";
  }
  return errs;
});

const visibleErrors = computed(() => ({
  name: submitted.value || touched.name ? fieldErrors.value.name : null,
  figmaUrl: submitted.value || touched.figmaUrl ? fieldErrors.value.figmaUrl : null,
  mobileFigmaUrl:
    submitted.value || touched.mobileFigmaUrl ? fieldErrors.value.mobileFigmaUrl : null,
}));

const canSubmit = computed(() => Object.values(fieldErrors.value).every(e => !e));
const stepMessage = computed(() => {
  if (step.value === Step.POLLING) return "Processing your design…";
  if (step.value === Step.DONE)
    return isExistingPrompt.value ? "Prompt updated successfully!" : "Prompt created successfully!";
  if (step.value === Step.ERROR) return "Something went wrong";
  return "";
});
const humanStatus = computed(() => STATUS_LABELS[statusDetail.value] || statusDetail.value || "");

const cleanup = () => {
  if (pollTimer.value) {
    clearTimeout(pollTimer.value);
    pollTimer.value = null;
  }
};

onUnmounted(cleanup);

const open = () => {
  step.value = Step.FORM;
  form.value = {
    name: props.initialName ?? "Figma Import",
    figmaUrl: props.initialFigmaUrl ?? "",
    mobileFigmaUrl: props.initialMobileFigmaUrl ?? "",
  };
  submitted.value = false;
  touched.name = false;
  touched.figmaUrl = false;
  touched.mobileFigmaUrl = false;
  workflowId.value = null;
  resultPathId.value = null;
  statusDetail.value = null;
  errorMessage.value = null;
  pollCount.value = 0;
  dialogRef.value?.open();
  if (props.initialFigmaUrl) submit();
};

const close = () => {
  cleanup();
  step.value = Step.FORM;
  submitted.value = false;
  touched.name = false;
  touched.figmaUrl = false;
  touched.mobileFigmaUrl = false;
};

const schedulePoll = () => {
  pollTimer.value = setTimeout(() => poll(), POLL_INTERVAL_MS);
};

const poll = async () => {
  if (step.value !== Step.POLLING) return;
  if (pollCount.value >= MAX_POLL_ATTEMPTS) {
    step.value = Step.ERROR;
    errorMessage.value = "Processing timed out. Please try again.";
    return;
  }
  pollCount.value++;
  try {
    const job = await ApiFigma.getPipelineJob(props.app.id, workflowId.value);
    if (step.value !== Step.POLLING) return;
    statusDetail.value = job.status;
    if (TERMINAL.has(job.status)) {
      if (job.status === "completed") {
        resultPathId.value = job.path_id;
        step.value = Step.DONE;
        emit("done", { pathId: job.path_id, result: job.result });
      } else {
        step.value = Step.ERROR;
        errorMessage.value = job.error || "Workflow did not complete successfully.";
      }
    } else {
      schedulePoll();
    }
  } catch (e) {
    if (step.value !== Step.POLLING) return;
    step.value = Step.ERROR;
    errorMessage.value = e.message || "Failed to check workflow status.";
  }
};

const submit = async () => {
  submitted.value = true;
  if (!canSubmit.value) return;
  step.value = Step.POLLING;
  statusDetail.value = "queued";
  try {
    let response;
    if (isRefine.value) {
      response = await ApiFigma.refinePipeline(props.app.id, {
        pathId: props.promptId,
        figmaUrl: form.value.figmaUrl.trim(),
        mobileFigmaUrl: form.value.mobileFigmaUrl.trim(),
      });
    } else {
      response = await ApiFigma.createPipeline(props.app.id, {
        name: form.value.name.trim(),
        figmaUrl: form.value.figmaUrl.trim(),
        mobileFigmaUrl: form.value.mobileFigmaUrl.trim(),
        pathId: props.promptId || undefined,
        pathGroupId: props.promptId ? undefined : props.pathGroupId || undefined,
        pathType: props.pathType,
        deviceType: props.deviceType,
      });
    }
    if (step.value !== Step.POLLING) return;
    workflowId.value = response.workflow_id;
    schedulePoll();
  } catch (e) {
    if (step.value !== Step.POLLING) return;
    step.value = Step.ERROR;
    errorMessage.value = e.message || "Failed to start workflow.";
  }
};

const navigate = () => {
  if (resultPathId.value) {
    const route = props.pathGroupId
      ? `/apps/${props.app.id}/placements/${props.pathGroupId}/promotions/${resultPathId.value}/edit`
      : `/apps/${props.app.id}/retentions/${resultPathId.value}/edit`;
    router.push(route);
    dialogRef.value?.close();
  }
};

const reset = () => {
  cleanup();
  step.value = Step.FORM;
  submitted.value = false;
  touched.name = false;
  touched.figmaUrl = false;
  touched.mobileFigmaUrl = false;
  workflowId.value = null;
  resultPathId.value = null;
  errorMessage.value = null;
  pollCount.value = 0;
};

const handleConfirmButton = computed(() => {
  if (step.value === Step.FORM) {
    return {
      onConfirm: submit,
      disabled:
        !canSubmit.value &&
        (Object.values(fieldErrors.value).find(Boolean) || "Please fill in all required fields"),
      confirmText: "Import",
    };
  } else if (step.value === Step.DONE) {
    return {
      onConfirm: navigate,
      confirmText: isExistingPrompt.value ? "View prompt" : "Edit prompt",
    };
  } else if (step.value === Step.ERROR) {
    return { onConfirm: reset, confirmText: "Try again" };
  }
  return { hideConfirm: true };
});

defineExpose({ open });
</script>
